import React, { useEffect, useMemo, useState } from "react";
import {
  Input,
  Label,
  Button,
  Form,
  FormFeedback,
  Alert,
} from "reactstrap";
import AuthShell from "../../Components/Common/AuthShell";
import { useSelector, useDispatch } from "react-redux";
import { withRouter, Link, Redirect } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { loginUser } from "../../store/actions";
import GoogleFirebase from "../../Components/GoogleFireBase";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";
import Turnstile, { useTurnstile } from "react-turnstile";
import { useBranding } from "../../Components/branding/Branding";
import { getDeviceFingerprint } from "../../helpers/deviceFingerprint";
import { getLoginPositionIfAllowed } from "../../helpers/loginPosition";
import { getValidLoggedinUser } from "../../helpers/authToken";

const sitekey = "0x4AAAAAAAhZMwfJAD7cG6kE";

const Login = (props) => {
  const dispatch = useDispatch();
  const { isBrandingActive } = useBranding();

  const [userLogin] = useState([]);
  const [FCMToken, setFCMToken] = useState(null);
  const [cfToken, setCfToken] = useState("");

  const { error } = useSelector((state) => ({
    error: state.Login.error,
  }));
  const turnstile = useTurnstile();

  const resetTurnstile = () => {
    setCfToken(""); // IMPORTANT: stop reusing token
    turnstile?.reset?.(); // reset widget to get a fresh token
  };

  // ----- environment / platform detection -----
  const env = useMemo(() => {
    const ua =
      typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    const maxTouchPoints =
      typeof navigator !== "undefined" ? navigator.maxTouchPoints || 0 : 0;

    const hostname =
      typeof window !== "undefined" ? window.location.hostname : "";

    // RN WebView flag (your mobile app sets this automatically)
    const isRNWebView =
      typeof window !== "undefined" && !!window.ReactNativeWebView;

    // iOS mobile (Safari / iPhone / iPad)
    const iOS =
      /iPhone|iPod/.test(ua) ||
      ua.includes("iPad") ||
      (typeof navigator !== "undefined" &&
        navigator.platform === "MacIntel" &&
        maxTouchPoints > 1);

    const isMobileUA = /Mobi|Android|iPhone|iPod|iPad/i.test(ua);
    const isIOSMobile = isMobileUA && iOS;

    // local-like hosts (localhost, emulator, LAN IP)
    const isLocalHost =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "10.0.2.2" ||
      hostname.endsWith(".local");

    const isLanIp =
      /^192\.168\./.test(hostname) ||
      /^10\./.test(hostname) ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);

    const isLocalLike = isLocalHost || isLanIp;

    const useTurnstile = process.env.REACT_APP_USE_TURNSTILE === "true";

    // ✅ Turnstile should ONLY appear on real domains + normal web browser
    // (never in RN WebView; never on localhost/LAN; optionally skip iOS mobile)
    const shouldUseTurnstile = useTurnstile && !isRNWebView && !isIOSMobile;

    return {
      ua,
      hostname,
      isRNWebView,
      isIOSMobile,
      isLocalLike,
      useTurnstile,
      shouldUseTurnstile,
    };
  }, []);

  // if Turnstile required -> must have cfToken
  const isTurnstileOk = !env.shouldUseTurnstile || !!cfToken;

  // ----- toast errors -----
  useEffect(() => {
    if (isEmpty(error)) return;

    if (error?.status !== 200) {
      toast.error(error?.message);

      if (env.shouldUseTurnstile) {
        resetTurnstile();
      }
    }
  }, [error, env.shouldUseTurnstile]); // include flag so it’s correct if env changes

  // ----- formik -----
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: userLogin.email || "",
      password: userLogin.password || "",
      remember: true,
      DeviceFingerprint: FCMToken,
      EntryUrl: typeof window !== "undefined" ? window.location.href : "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: async (values) => {
      // Block submit if Turnstile is required but not completed
      if (!isTurnstileOk) return;

      // TraceTail device fingerprint (never blocks login — resolves to null on failure).
      const fp = await getDeviceFingerprint();
      if (fp?.visitorId) {
        try {
          localStorage.setItem("tracetail_visitor_id", fp.visitorId);
        } catch (_) {
          // ignore storage errors
        }
      }

      // Change #100668 - location for the sign-in log, only if already allowed.
      const position = await getLoginPositionIfAllowed();

      const payload = {
        ...values,
        remember: !!values.remember,
        ...(env.shouldUseTurnstile ? { cf_turnstile_token: cfToken } : {}),
        User_Agent: env.ua,
        TracetailVisitorId: fp?.visitorId || null,
        TracetailConfidence: fp?.confidence ?? null,
        ...position,
      };

      dispatch(loginUser(payload, props.history));
      if (env.shouldUseTurnstile) resetTurnstile();
    },
  });

  const [showPassword, setShowPassword] = useState(false);

  document.title = `SignIn | ${isBrandingActive ? "CRMit - " : ""}Less Work. Do More.`;

  // Already signed in with a valid (unexpired) token? Don't show the login form -
  // send them on to their opening page. getValidLoggedinUser() also clears the
  // session if the stored token is expired, so a stale token falls through to
  // the form rather than bouncing to a protected page. Declared after all hooks
  // to keep hook order stable.
  const validUser = getValidLoggedinUser();
  if (validUser) {
    const dest = validUser?.get_staff_data?.OpeningUrl || "/dashboard";
    return <Redirect to={dest} />;
  }

  return (
    <React.Fragment>
      {/* ✅ Firebase messaging only on normal web browser (never in RN WebView) */}
      {!env.isRNWebView && <GoogleFirebase setFCMToken={setFCMToken} />}

      <AuthShell
        heading="Sign In"
        subheading="Sign in to continue to your account"
        below={
          <p className="mb-0">
            Don&apos;t have an account?{" "}
            <a
              href="https://crmit.app/signup"
              target="_blank"
              rel="noreferrer"
            >
              Signup
            </a>
          </p>
        }
      >
        <Form
          autoComplete="on"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <div className="mb-3">
            <Label htmlFor="email" className="form-label">
              Email
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Enter email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              value={validation.values.email}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={
                validation.touched.email && !!validation.errors.email
              }
            />
            <FormFeedback>{validation.errors.email}</FormFeedback>
          </div>

          <div className="mb-3">
            <Label htmlFor="password" className="form-label">
              Password
            </Label>

            <div className="position-relative auth-pass-inputgroup mb-3">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                autoComplete="current-password"
                value={validation.values.password}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.password &&
                  !!validation.errors.password
                }
                className="form-control pe-5"
              />
              <FormFeedback>{validation.errors.password}</FormFeedback>

              <button
                type="button"
                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                onClick={() => setShowPassword((p) => !p)}
                aria-label={
                  showPassword ? "Hide password" : "Show password"
                }
              >
                <i className="ri-eye-fill align-middle"></i>
              </button>
            </div>
          </div>

          <div className="form-check mb-3">
            <Input
              className="form-check-input"
              type="checkbox"
              id="remember-check"
              name="remember"
              checked={validation.values.remember}
              onChange={validation.handleChange}
            />
            <Label className="form-check-label" htmlFor="remember-check">
              Remember me
            </Label>
          </div>

          {/* ✅ Turnstile only when appropriate (real domain + web browser) */}
          {env.shouldUseTurnstile && (
            <div className="mb-3">
              <Turnstile
                sitekey={sitekey}
                onVerify={(token) => setCfToken(token)}
                onExpire={() => resetTurnstile()}
                onError={() => resetTurnstile()}
                theme="light"
                refreshExpired="auto"
                size="flexible"
              />
            </div>
          )}

          <div className="mt-4">
            {/* only show the alert block when turnstile is active */}
            {env.shouldUseTurnstile && error?.message && (
              <Alert color="danger">{error?.message}</Alert>
            )}

            <Button
              color="primary"
              className="w-100"
              type="submit"
              disabled={!isTurnstileOk}
              title={
                !isTurnstileOk
                  ? "Please complete the Cloudflare check"
                  : undefined
              }
            >
              Sign In
            </Button>
          </div>

          <div className="mt-3 text-end">
            <Link to="/forgot-password" className="text-muted">
              Forgot password?
            </Link>
          </div>
        </Form>
      </AuthShell>
    </React.Fragment>
  );
};

export default withRouter(Login);
